import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";

import { normalizeText } from "../utils/messageHelpers";

const EMPTY_PAYLOAD = {
  text: "",
  quoteMessageId: null,
  attachment: null,
  scheduleAt: "",
};

/**
 * Estado e ações da caixa de mensagem da conversa aberta: texto, resposta,
 * anexo, agendamento, envio de texto/áudio e o único ponto que trata colar ou
 * arrastar arquivo (print) — na caixa ou em qualquer lugar da tela.
 */
export function useMessageComposer({
  selectedChat,
  user,
  sendAction,
  addAttachment,
  audioBlob,
  cancelRecording,
}) {
  const [payload, setPayload] = useState(EMPTY_PAYLOAD);
  const [activePanel, setActivePanel] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  // Print/arquivo colado ou arrastado ainda subindo: enviar antes mandaria a
  // mensagem sem ele.
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const messageInputRef = useRef(null);

  // Trocar de conversa descarta o rascunho da anterior.
  useEffect(() => {
    setPayload(EMPTY_PAYLOAD);
  }, [selectedChat?.id]);

  useEffect(() => {
    if (selectedChat && messageInputRef.current) {
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 0);
    }
  }, [selectedChat]);

  const updatePayload = useCallback((changes) => {
    setPayload((prev) => ({ ...prev, ...changes }));
  }, []);

  const togglePanel = useCallback((panel) => {
    setActivePanel((current) => (current === panel ? null : panel));
  }, []);

  const sendMessage = useCallback(async () => {
    if (uploadingAttachment) {
      toast("Aguarde o anexo terminar de carregar.", { icon: "⏳" });
      return;
    }
    if (!payload.text.trim() && !payload.attachment) return;
    if (!selectedChat) return;
    const normalizedText = normalizeText(payload.text);

    if (!normalizedText && !payload.attachment) return;

    setSendingMessage(true);

    const hasText = payload.text.trim().length > 0;
    const message = hasText ? payload.text.replace(/\r\n/g, "\n") : "Envio de Anexo";

    try {
      const success = sendAction("send_message", {
        conversationId: selectedChat.id,
        userId: user.id || null,
        username: user.name || null,
        phoneNumber: selectedChat.contact.phoneNumber,
        message,
        quoteMessageId: payload?.quoteMessageId || null,
        attachment: payload?.attachment || null,
        scheduleAt: payload?.scheduleAt || null,
      });
      if (!success) throw new Error("Falha ao enviar mensagem.");

      setPayload(EMPTY_PAYLOAD);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      toast.error("Erro ao enviar mensagem.");
    } finally {
      setSendingMessage(false);
    }
  }, [payload, selectedChat, sendAction, uploadingAttachment, user.id, user.name]);

  const sendAudioMessage = useCallback(async () => {
    if (!audioBlob || !selectedChat) return;

    setSendingMessage(true);

    try {
      const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, {
        type: "audio/webm;codecs=opus",
      });

      // O áudio sobe como anexo oculto (não aparece na biblioteca de Anexos).
      const formData = new FormData();
      formData.append("name", audioFile.name);
      formData.append("userIds", user.id);
      formData.append("hidden", true);
      formData.append("file", audioFile);

      const attach = await addAttachment(formData);

      const success = sendAction("send_message", {
        conversationId: selectedChat.id,
        userId: user.id || null,
        username: user.name || null,
        phoneNumber: selectedChat.contact.phoneNumber,
        message: "audio",
        quoteMessageId: payload?.quoteMessageId || null,
        attachment: attach.filePath,
        scheduleAt: payload?.scheduleAt || null,
      });
      if (!success) throw new Error("Falha ao enviar áudio.");

      cancelRecording();

      if (payload?.scheduleAt) {
        toast.success(
          "Áudio agendado para " + new Date(payload.scheduleAt).toLocaleString("pt-BR") + "!",
        );
      } else {
        toast.success("Áudio enviado!");
      }
    } catch (err) {
      console.error("Erro ao enviar áudio:", err);
      toast.error("Erro ao enviar áudio.");
    } finally {
      setSendingMessage(false);
    }
  }, [addAttachment, audioBlob, cancelRecording, payload, selectedChat, sendAction, user.id, user.name]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  // hidden: anexo avulso desta conversa (print colado, arquivo arrastado), que
  // não deve aparecer na biblioteca de Anexos para a equipe — como o áudio.
  const uploadAttachment = useCallback(
    async (file, { hidden = false } = {}) => {
      // Print colado chega sempre como "image.png": dá um nome que identifica.
      const isPastedImage = /^image\.(png|jpe?g|gif|webp)$/i.test(file.name || "");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      const name = isPastedImage ? `print-${stamp}.${file.name.split(".").pop()}` : file.name;

      setUploadingAttachment(true);
      try {
        const formData = new FormData();

        formData.append("name", name);
        formData.append("userIds", user.id);
        if (hidden) formData.append("hidden", true);
        formData.append("file", file, name);

        const attach = await addAttachment(formData);

        setPayload((prev) => ({ ...prev, attachment: attach.filePath }));
        setActivePanel(null);
        messageInputRef.current?.focus();
      } catch (err) {
        console.error("Erro ao enviar anexo:", err);
        toast.error(`Não foi possível anexar o arquivo: ${err.message || "erro desconhecido"}`);
      } finally {
        setUploadingAttachment(false);
      }
    },
    [addAttachment, user.id],
  );

  const handlePaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items || [];

      for (const item of items) {
        if (item.kind === "file") {
          const file = item.getAsFile();

          if (file) {
            uploadAttachment(file, { hidden: true });
            e.preventDefault();
            break;
          }
        }
      }
    },
    [uploadAttachment],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      if (file) uploadAttachment(file, { hidden: true });
    },
    [uploadAttachment],
  );

  // Ctrl+V de print com a conversa aberta funciona mesmo sem o cursor na caixa
  // de mensagem. Colar em outro campo (busca, observações...) segue normal; o
  // colar na própria caixa já é tratado por ela (defaultPrevented).
  const handlePasteRef = useRef(handlePaste);
  handlePasteRef.current = handlePaste;
  useEffect(() => {
    if (!selectedChat?.id) return undefined;
    const onDocumentPaste = (e) => {
      if (e.defaultPrevented) return;
      const target = e.target;
      const editable =
        target?.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target?.tagName);
      if (editable) return;
      handlePasteRef.current(e);
    };
    document.addEventListener("paste", onDocumentPaste);
    return () => document.removeEventListener("paste", onDocumentPaste);
  }, [selectedChat?.id]);

  const selectQuickMessage = useCallback((qm) => {
    setPayload((prev) => ({ ...prev, text: qm.content, attachment: qm.attachmentUrl || null }));
    setActivePanel(null);
  }, []);

  const selectExistingAttachment = useCallback((att) => {
    setPayload((prev) => ({ ...prev, attachment: att.filePath }));
    setActivePanel(null);
  }, []);

  const selectQuoteMessage = useCallback((messageId) => {
    setPayload((prev) => ({ ...prev, quoteMessageId: messageId }));
  }, []);

  const appendEmote = useCallback((emoji) => {
    setPayload((prev) => ({ ...prev, text: prev.text + emoji }));
  }, []);

  const cancelScheduledMessage = useCallback(
    (jobId) => {
      if (!jobId) return;
      sendAction("cancel_scheduled_message", { jobId });
    },
    [sendAction],
  );

  return {
    payload,
    updatePayload,
    activePanel,
    togglePanel,
    sendingMessage,
    uploadingAttachment,
    messageInputRef,
    sendMessage,
    sendAudioMessage,
    handleKeyDown,
    uploadAttachment,
    handlePaste,
    handleDrop,
    selectQuickMessage,
    selectExistingAttachment,
    selectQuoteMessage,
    appendEmote,
    cancelScheduledMessage,
  };
}
